/**
 * Coordinates the full-screen loading veil (`body.loading`) so that fast reads
 * never flash it while writes and slow reads still get it (#111741).
 *
 * Two independent owners share one DOM effect:
 *  - "counter": the http mixin's page-global active-request count (processData)
 *  - "direct":  explicit showLoading()/hideLoading() calls outside that path
 * An owner's hide() never disturbs the other owner's pending timer or applied
 * state; the veil is cleared only when neither owner needs it.
 *
 * Pure: DOM effects and timers are injected, so it is unit-testable and the
 * single instance can live on `window` to be shared by every bundle on a page.
 */
const OWNERS = ["counter", "direct"];

function createLoadingOverlay({
    apply,
    clear,
    delayMs = 400,
    setTimeoutFn = setTimeout,
    clearTimeoutFn = clearTimeout,
}) {
    // per owner: "idle" | "pending" (timer armed) | "applied"
    const state = { counter: "idle", direct: "idle" };
    const timers = { counter: null, direct: null };
    let applied = false;

    function anyApplied() {
        return OWNERS.some((o) => state[o] === "applied");
    }
    function anyPending() {
        return OWNERS.some((o) => state[o] === "pending");
    }
    function cancelTimer(owner) {
        if (timers[owner] !== null) {
            clearTimeoutFn(timers[owner]);
            timers[owner] = null;
        }
    }
    function applyFor(owner) {
        cancelTimer(owner);
        state[owner] = "applied";
        if (!applied) {
            applied = true;
            apply();
        }
    }
    function sync() {
        if (applied && !anyApplied()) {
            applied = false;
            clear();
        }
    }

    return {
        /**
         * @param {"counter"|"direct"} owner
         * @param {{immediate?: boolean}} [opts] immediate → veil now; otherwise arm this
         *        owner's timer (once) and veil only if still needed when it fires.
         */
        show(owner, { immediate = false } = {}) {
            if (immediate) {
                applyFor(owner);
                return;
            }
            if (state[owner] !== "idle") {
                return; // already pending or applied for this owner
            }
            state[owner] = "pending";
            timers[owner] = setTimeoutFn(() => {
                timers[owner] = null;
                if (state[owner] === "pending") {
                    applyFor(owner);
                }
            }, delayMs);
        },
        /** A write began: anything still waiting on a timer must veil right now. */
        promote() {
            OWNERS.forEach((o) => {
                if (state[o] === "pending") {
                    applyFor(o);
                }
            });
        },
        hide(owner) {
            cancelTimer(owner);
            state[owner] = "idle";
            sync();
        },
        /** True while any owner is pending or applied — "a request is in flight". */
        busy() {
            return anyPending() || anyApplied();
        },
        visible() {
            return applied;
        },
    };
}

module.exports = { createLoadingOverlay };
